import React from "react";
import { Link } from "react-router-dom";
import Header from "@/components/landing/Header";
import SectionShell from "@/components/landing/SectionShell";
import PageFooter from "@/components/landing/PageFooter";

/**
 * 4 napos élményprogram — new page.
 * Uses only existing design primitives (SectionShell, hairlines, Cormorant + Inter,
 * gold accents) so it feels like it was part of the site from day one.
 */
export default function ExperienceProgram() {
  return (
    <main
      data-testid="experience-program-page"
      className="bg-warm text-graphite min-h-screen"
    >
      <Header />
      <div className="pt-24 md:pt-28" />

      {/* HERO — big image, short opening thought */}
      <section
        data-testid="experience-hero"
        className="relative px-6 md:px-10 py-6 md:py-10"
      >
        <div className="max-w-3xl mx-auto text-center">
          <h1
            data-testid="experience-hero-title"
            className="font-serif-display text-[38px] sm:text-[48px] md:text-[58px] lg:text-[64px] leading-[1.1] text-graphite tracking-tight mb-8 md:mb-10"
          >
            4 napos{" "}
            <em className="text-gold" style={{ fontStyle: "italic" }}>
              élményprogram
            </em>
          </h1>
          <p className="font-serif-display italic text-graphite text-[20px] sm:text-[22px] md:text-[24px] leading-[1.6] max-w-xl mx-auto">
            Négy nap, ahol az önismeret nem feltétlenül nehéz vagy fájdalmas út,
            <br className="hidden md:block" />
            hanem <span className="text-gold">könnyed, szép és nőies</span>{" "}
            kapcsolódás.
          </p>
        </div>

        {/* Hero image — natural aspect, subtle warm frame */}
        <figure className="mt-8 md:mt-10 max-w-5xl mx-auto">
          <div className="portrait-frame relative">
            <img
              data-testid="experience-hero-image"
              src="/images/elmenyprogram-hero.jpg"
              alt="Női kör, közös élmény, kapcsolódás — 4 napos élményprogram"
              className="w-full h-auto block"
              style={{ filter: "saturate(0.98) contrast(1.02)" }}
              loading="eager"
            />
          </div>
        </figure>
      </section>

      {/* MI LENNE, HA — philosophical opening */}
      <SectionShell
        id="mi-lenne-ha"
        testId="experience-what-if"
        titleStart="Mi lenne,"
        titleAccent="ha…"
        tight
      >
        <p>
          Mindannyian ismerünk történeteket, ahol egy veszteség, egy betegség vagy egy
          mély krízis teljesen új irányt adott valakinek. Én magam is megtapasztaltam,
          hogy a legnehezebb időszakok milyen mély felismeréseket hozhatnak. Nem
          szeretném elvenni ezeknek az élményeknek az értékét. Mégis egy kérdés évek óta
          újra és újra visszatér hozzám.
        </p>

        <h3
          data-testid="experience-what-if-question"
          className="font-serif-display text-[26px] sm:text-[30px] md:text-[34px] leading-[1.25] text-graphite text-center max-w-xl mx-auto my-12 md:my-14"
        >
          Valóban ez az{" "}
          <em className="text-gold" style={{ fontStyle: "italic" }}>
            egyetlen
          </em>{" "}
          út?
        </h3>

        <p>
          Valóban csak akkor engedünk be új lehetőségeket, amikor az élet már annyira
          szorít, hogy nincs más választásunk? Vagy létezik egy másik lehetőség is? Egy
          út, ahol nem a szenvedés indítja el a változást, hanem a{" "}
          <span className="text-gold">tudatos választás</span>.
        </p>
      </SectionShell>

      {/* FIRST CTA — soft invitation */}
      <ExperienceCTA
        testId="experience-cta-first"
        text={
          <>
            Ha úgy érzed, nem szeretnéd megvárni, amíg az élet kényszerít egy új irányra,
            <br className="hidden md:block" />
            <em className="text-gold" style={{ fontStyle: "italic" }}>
              szeretettel várlak
            </em>
            .
          </>
        }
      />

      {/* HISZEK EGY MÁSIK LEHETŐSÉGBEN */}
      <SectionShell
        id="masik-lehetoseg"
        testId="experience-belief"
        titleStart="Hiszek egy másik"
        titleAccent="lehetőségben"
        tinted
        tight
      >
        <p>
          Hiszek abban, hogy nem kell megvárnunk, amíg az élet megállít. Nem kell mindig
          a végletekig elmennünk ahhoz, hogy új irányt válasszunk. Hiszek abban, hogy
          amikor láthatóvá válik egy működés, megszületik a lehetőség arra, hogy másképp
          rendezzük össze azt, ami eddig automatikusan működtetett minket. Nem azért,
          hogy megtagadjuk azt, akik eddig voltunk, hanem hogy teret adjunk annak az új
          minőségnek, amely talán már régóta szeretne megszületni bennünk. Mert a valódi
          változás nem kívül kezdődik. Először belül születik meg, és ebből épül fel
          fokozatosan egy új működés, új kapcsolódások és végül egy új életminőség.
        </p>
      </SectionShell>

      {/* LEHET, HOGY — self-recognition */}
      <SectionShell
        id="lehet-hogy"
        testId="experience-maybe"
        titleStart="Lehet,"
        titleAccent="hogy…"
        tight
      >
        <p>
          Lehet, hogy már rengeteget dolgoztál önmagadon, mégis vannak helyzetek, ahol
          ugyanaz a reakció jelenik meg újra és újra. Lehet, hogy kívülről erősnek
          látszol, belül mégis érzed, hogy túl sok energiát visz el a megfelelés, a
          kontroll vagy az állandó készenlét. Lehet, hogy már nem keresed a hibást,
          mégis hiányzik valami.
        </p>

        <aside
          data-testid="experience-maybe-cascade"
          className="my-8 md:my-10 mx-auto max-w-xl bg-warm border border-hairline px-7 md:px-10 py-7 md:py-8"
        >
          <p className="font-serif-display italic text-graphite text-[19px] md:text-[21px] leading-[1.9] text-center">
            Egy természetesebb könnyedség.
            <br />
            Egy mélyebb bizalom.
            <br />
            Egy olyan belső rend, ahol nem erőfeszítésből kell működnöd.
          </p>
        </aside>

        <p>
          Ha ezek közül bármelyik ismerős, lehet, hogy nem új válaszokra van szükséged.
        </p>
        <p className="text-center font-serif-display italic text-graphite text-[22px] md:text-[26px] leading-[1.4] mt-8">
          Hanem egy új{" "}
          <em className="text-gold not-italic" style={{ fontStyle: "italic" }}>
            térre
          </em>
          .
        </p>
      </SectionShell>

      {/* MI TÖRTÉNIK EZ ALATT A 4 NAP ALATT? */}
      <SectionShell
        id="mi-tortenik"
        testId="experience-four-days"
        titleStart="Mi történik ez alatt a négy"
        titleAccent="nap alatt?"
        tinted
        tight
      >
        <p>
          Nem a múltban keresünk válaszokat, és nem azért találkozunk, hogy újra átéljük
          a régi történeteket. A program során olyan teret hozunk létre, ahol ráláthatsz
          azokra a működésekre, amelyek ma is hatással vannak a kapcsolataidra. Nem
          azért, hogy elemezzük őket, hanem hogy lehetőséged legyen másképp kapcsolódni
          önmagadhoz és azokhoz, akik fontosak számodra. Amikor egy működés hosszú ideig
          változatlan marad, a következményei gyakran már jóval azelőtt kirajzolódnak,
          hogy láthatóvá válnának. Én ezeket a korai mintázatokat keresem.
        </p>
        <p className="text-center font-serif-display italic text-graphite text-[22px] md:text-[26px] leading-[1.4] mt-8">
          Mert ilyenkor még van tér. Van idő.
          <br />
          Van <em className="text-gold" style={{ fontStyle: "italic" }}>választás</em>.
        </p>
      </SectionShell>

      {/* NEM ÚJ DARABOKAT KERESÜNK — puzzle metaphor */}
      <SectionShell
        id="puzzle"
        testId="experience-puzzle"
        titleStart="Nem új darabokat"
        titleAccent="keresünk"
        tight
      >
        <p>
          Talán egész életünkben azt tanultuk, hogy a változáshoz valamitől meg kell
          szabadulnunk. Le kell tennünk. El kell engednünk. Meg kell javítanunk. Talán
          te is hallottad már ezt a mondatot: <em>„Engedd el.”</em> Vannak élethelyzetek,
          amikor ez nemhogy nem segít, hanem még nagyobb tehernek érződik. Mert amikor
          valami igazán fontos, azt nem lehet egyszerűen akaraterőből elengedni. Én
          ezért ezt teljesen máshonnan közelítem meg.
        </p>
        <p>
          Fontosnak tartom, hogy láthatóvá váljon mindaz, ami eddig automatikusan
          működött. Mert amikor megértjük az összefüggéseket, gyakran magától kezd
          átrendeződni az is, amit korábban görcsösen próbáltunk megváltoztatni. A
          legtöbbször nem új darabokra van szükségünk, hanem arra, hogy végre meglássuk
          azokat, amelyek már most is ott vannak.
        </p>
        <p className="text-center font-serif-display italic text-graphite text-[22px] md:text-[26px] leading-[1.4] my-10">
          A kirakó elemei nem hiányoznak, csak még nem álltak össze{" "}
          <em className="text-gold" style={{ fontStyle: "italic" }}>
            egésszé
          </em>
          .
        </p>
        <p>
          A 4 nap során először láthatóvá tesszük őket. Megnézzük, melyik darab milyen
          szerepet tölt be, és amikor tisztábban látjuk az összefüggéseket, lehetőség
          nyílik arra, hogy másképp rendeződjenek össze.
        </p>

        <aside
          data-testid="experience-puzzle-cascade"
          className="my-8 md:my-10 mx-auto max-w-xl bg-warm border border-hairline px-7 md:px-10 py-7 md:py-8"
        >
          <p className="font-serif-display italic text-graphite text-[19px] md:text-[21px] leading-[1.7] text-center">
            Nem <span className="text-gold">akarásból</span> vagy{" "}
            <span className="text-gold">erőből</span>, hanem mert teret adunk annak,
            hogy új rend jöhessen létre.
          </p>
        </aside>

        {/* Closing highlight — the key line, matching pull-quote size */}
        <div className="my-10 md:my-12 text-center">
          <p className="font-serif-display italic text-graphite text-[22px] md:text-[26px] leading-[1.45] max-w-xl mx-auto">
            Az egység nem attól születik meg, hogy minden{" "}
            <em className="text-gold" style={{ fontStyle: "italic" }}>
              tökéletes
            </em>
            .
            <br />
            Hanem attól, hogy minden a{" "}
            <em className="text-gold" style={{ fontStyle: "italic" }}>
              helyére
            </em>{" "}
            kerülhet.
          </p>
        </div>
      </SectionShell>

      {/* VAN BENNÜNK EGY RÉSZ — the quiet feminine quality */}
      <SectionShell
        id="van-bennunk-egy-resz"
        testId="experience-inner-part"
        titleStart="Van bennünk egy"
        titleAccent="rész…"
        tight
      >
        <p>
          Van bennünk egy rész, amelyet a hétköznapok zajában könnyű szem elől
          téveszteni. Nem azért, mert eltűnik, hanem mert ritkán adunk neki valódi
          teret. Talán éppen ezért érezzük néha azt, hogy hiába teszünk meg mindent,
          mégis túl sok erőfeszítésbe kerülnek azok a dolgok, amelyekről legbelül
          tudjuk, hogy természetesebben is működhetnének.
        </p>
        <p>
          Én hiszek abban, hogy létezik bennünk egy olyan női minőség, amely nem a
          folyamatos helytállásból, nem a bizonyításból és nem a küzdelemből születik,
          hanem a <span className="text-gold">belső kapcsolódásból</span>. Abból a
          csendes erőből, amely nem akar mindenáron irányítani, mégis képes új rendet
          teremteni. Vannak pillanatok, amikor kívülről úgy tűnik, mintha minden magától
          rendeződne. Mintha a megfelelő emberek a megfelelő időben érkeznének és
          könnyebbé válna az, ami korábban folyamatos erőfeszítést igényelt.
        </p>
        <p>
          Sokan ezt varázslatnak nevezik. Így is hívhatjuk. Ugyanakkor ez valahol annak
          a természetes következménye, amikor belül új rend születik és a puzzle
          darabjai lassan a helyükre kerülnek. Egyszer csak elindul valami. Nem mindig
          látványosan és egyik napról a másikra. Egyszer csak azt veszed észre, hogy
          más döntéseket hozol, másképp kapcsolódsz, más emberek jelennek meg az
          életedben és más lehetőségek találnak meg. Amikor egy idő után visszanézel,
          talán te is azt mondod majd:
        </p>

        {/* Reader's inner voice — framed pull-quote (matches cascade box style) */}
        <aside
          data-testid="experience-inner-part-quote"
          className="my-8 md:my-10 mx-auto max-w-xl bg-warm border border-hairline px-7 md:px-10 py-7 md:py-8"
        >
          <p className="font-serif-display italic text-graphite text-[19px] md:text-[21px] leading-[1.7] text-center">
            „Érdekes… nem tudom pontosan hogyan… de valahogy kezd minden a{" "}
            <em className="text-gold" style={{ fontStyle: "italic" }}>
              helyére
            </em>{" "}
            kerülni.”
          </p>
        </aside>

        <p className="text-center font-serif-display italic text-graphite text-[22px] md:text-[26px] leading-[1.55] mt-8 max-w-xl mx-auto">
          Ez a négy nap számomra erről szól. Egy{" "}
          <em className="text-gold" style={{ fontStyle: "italic" }}>
            belső utazásról
          </em>
          , amelynek hatása messze túlmutat azon az időn, amit együtt töltünk.
        </p>
      </SectionShell>

      {/* MITŐL KÜLÖNLEGES EZ A 4 NAP — female community, celebration */}
      <SectionShell
        id="miert-kulonleges"
        testId="experience-special"
        titleStart="Mitől"
        titleAccent="különleges?"
        tinted
        tight
      >
        <p className="text-center font-serif-display italic text-graphite text-[22px] md:text-[26px] leading-[1.4] mb-10">
          Mert négy napon keresztül tudatosan{" "}
          <em className="text-gold" style={{ fontStyle: "italic" }}>
            önmagadat
          </em>{" "}
          is választod.
        </p>
        <p>
          A legtöbb nő hihetetlen dolgokra képes. Gondoskodik. Szervez. Figyel. Megtart.
          Fejlődik. Olvas. Tanul. Helytáll. Mindeközben elfelejt ünnepelni. Elfelejt
          megállni egy pillanatra, és észrevenni, milyen utat tett meg. Elfelejti, hogy
          ő maga is fontos. Pedig az önismeretnek nem kell feltétlenül nehéz vagy
          fájdalmas útnak lennie.
        </p>

        <aside
          data-testid="experience-special-cascade"
          className="my-8 md:my-10 mx-auto max-w-xl bg-warm border border-hairline px-7 md:px-10 py-7 md:py-8"
        >
          <p className="font-serif-display italic text-graphite text-[19px] md:text-[21px] leading-[1.7] text-center">
            Lehet <span className="text-gold">könnyedebb</span>,{" "}
            <span className="text-gold">szép</span>,{" "}
            <span className="text-gold">nőies</span> és belső sikereket{" "}
            <span className="text-gold">ünneplő</span> folyamat. Ami mégis mély{" "}
            <span className="text-gold">transzformációt</span> indít el.
          </p>
        </aside>

        <p>
          Ez a 4 napos, napközbeni élményprogram egy biztonságos tér létrehozásáról szól.
          Egy olyan közösségről, ahol nemcsak önmagaddal találkozhatsz mélyebben, hanem
          olyan nőkkel is, akik ugyanúgy hisznek a tudatos választás erejében. Mert
          attól, hogy édesanya lettél, nem kell háttérbe kerülnie annak a nőnek, aki
          mindig is voltál.
        </p>
        <p className="text-center font-serif-display italic text-graphite text-[22px] md:text-[26px] leading-[1.4] mt-10">
          Fontos vagy annyira, hogy időt, figyelmet és energiát adj{" "}
          <em className="text-gold" style={{ fontStyle: "italic" }}>
            önmagadnak
          </em>
          .
        </p>
      </SectionShell>

      {/* EGYSÉGBEN AZ ERŐ — family reflection, before practical details */}
      <FamilyUnity />

      {/* GYAKORLATI INFORMÁCIÓK */}
      <PracticalInfo />

      {/* CLOSING CTA */}
      <ExperienceCTA
        testId="experience-cta-closing"
        variant="closing"
        text={
          <>
            Ha úgy érzed, itt az ideje, hogy ne csak helytállj, hanem önmagadnak is teret
            adj,
            <br className="hidden md:block" />
            <em className="text-gold" style={{ fontStyle: "italic" }}>
              szeretettel várlak
            </em>
            .
          </>
        }
      />

      <PageFooter />
    </main>
  );
}

/* --- inline helpers ---------------------------------------------------- */

function ExperienceCTA({ testId, text, variant = "default" }) {
  const isClosing = variant === "closing";
  return (
    <section
      data-testid={testId}
      className={`px-6 md:px-10 ${
        isClosing
          ? "py-7 md:py-10 bg-tinted border-t border-hairline"
          : "py-5 md:py-7 bg-warm"
      }`}
    >
      <div className="max-w-2xl mx-auto text-center">
        <p
          className={`font-serif-display text-graphite text-[22px] sm:text-[26px] md:text-[30px] leading-[1.5] max-w-xl mx-auto ${
            isClosing ? "mb-6 md:mb-7" : "mb-5"
          }`}
        >
          {text}
        </p>
        <Link
          to="/elmenyprogram/jelentkezes"
          data-testid={`${testId}-link`}
          className="inline-flex items-center gap-3 text-[12px] tracking-[0.3em] uppercase text-graphite hover:text-gold transition-colors duration-300 font-body pb-1 border-b border-gold"
        >
          {isClosing ? "Jelentkezem" : "Jelentkezem"}
          <span aria-hidden="true">→</span>
        </Link>
      </div>
    </section>
  );
}

function PracticalInfo() {
  const earlyBirdDeadline = new Date("2026-08-01T23:59:59+02:00");
  const now = new Date();
  const msLeft = earlyBirdDeadline - now;
  const daysLeft = Math.max(0, Math.ceil(msLeft / (1000 * 60 * 60 * 24)));

  const rows = [
    {
      label: "Időpont",
      value: "2026. augusztus 6–9.",
    },
    {
      label: "Helyszín",
      value: "Debrecen",
    },
    {
      label: "Program",
      value:
        "4 egymást követő nap, napközbeni élményprogram. Az esték szabadok — mindenki otthon pihenhet, és a saját ritmusában integrálhatja a nap során megélt élményeket.",
    },
    {
      label: "Létszám",
      value:
        "Kis létszámú csoport, hogy valódi figyelem, biztonság és személyes kapcsolódás jöhessen létre.",
    },
  ];

  return (
    <section
      id="gyakorlati"
      data-testid="experience-practical"
      className="relative pt-6 pb-10 md:pt-8 md:pb-14 px-6 md:px-10"
    >
      <div className="max-w-2xl mx-auto">
        <h2 className="font-serif-display text-[34px] sm:text-[40px] md:text-[46px] leading-[1.18] text-graphite mb-10 text-center">
          Technikai{" "}
          <em className="text-gold" style={{ fontStyle: "italic" }}>
            részletek
          </em>
        </h2>

        <dl className="space-y-8 md:space-y-10">
          {rows.map((r) => (
            <div
              key={r.label}
              className="grid grid-cols-1 md:grid-cols-[180px_1fr] gap-3 md:gap-8 pb-6 border-b border-hairline last:border-b-0"
            >
              <dt className="font-body text-[11px] tracking-[0.28em] uppercase text-gold pt-1">
                {r.label}
              </dt>
              <dd className="font-body text-graphite text-[16px] md:text-[17px] leading-[1.85] font-light">
                {r.value}
              </dd>
            </div>
          ))}
        </dl>

        {/* Pricing block — elegant, echoing consultation page pattern */}
        <div className="mt-14 md:mt-16">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 md:gap-8">
            {/* Early bird */}
            <div className="bg-warm border border-gold px-7 md:px-9 py-8 md:py-10 text-center relative">
              <p className="font-body text-[10px] tracking-[0.32em] uppercase text-gold mb-4">
                Early Bird
              </p>
              <p className="font-serif-display text-graphite text-[34px] md:text-[42px] leading-none tracking-tight">
                <span className="text-gold">99&thinsp;000&nbsp;Ft</span>
              </p>
              <p className="font-body text-[13px] md:text-[14px] text-muted-warm italic font-light mt-4 leading-[1.6]">
                2026. augusztus 1.
              </p>
              {daysLeft > 0 && (
                <div
                  data-testid="early-bird-countdown"
                  className="mt-6 pt-5 border-t border-hairline"
                >
                  <p className="font-body text-[10px] tracking-[0.28em] uppercase text-muted-warm mb-2">
                    Még
                  </p>
                  <p className="font-serif-display text-graphite leading-none">
                    <span
                      data-testid="early-bird-days-left"
                      className="text-[38px] md:text-[44px] text-gold tracking-tight"
                      style={{ fontStyle: "italic" }}
                    >
                      {daysLeft}
                    </span>{" "}
                    <span className="text-[15px] md:text-[16px] italic text-muted-warm font-light">
                      nap
                    </span>
                  </p>
                </div>
              )}
            </div>

            {/* Regular */}
            <div className="bg-warm border border-hairline px-7 md:px-9 py-8 md:py-10 text-center">
              <p className="font-body text-[10px] tracking-[0.32em] uppercase text-muted-warm mb-4">
                Ezt követően
              </p>
              <p className="font-serif-display text-graphite text-[34px] md:text-[42px] leading-none tracking-tight">
                198&thinsp;000&nbsp;Ft
              </p>
              <p className="font-body text-[13px] md:text-[14px] text-muted-warm italic font-light mt-4 leading-[1.6]">
                Teljes részvételi díj
              </p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

/* --------------------------------------------------------------------- */
/* FamilyUnity — the final reflection: "Egységben az erő"                 */
/* --------------------------------------------------------------------- */

function FamilyUnity() {
  return (
    <section
      id="egysegben-az-ero"
      data-testid="experience-family-unity"
      className="relative pt-10 pb-6 md:pt-14 md:pb-8 px-6 md:px-10 border-t border-hairline"
    >
      <div className="max-w-5xl mx-auto">
        {/* Symbolic banner — landscape, family + retreat identity */}
        <figure className="mb-10 md:mb-14 max-w-4xl mx-auto -mx-6 md:mx-auto">
          <div className="relative">
            <img
              data-testid="experience-family-unity-image"
              src="/images/elmenyprogram-egysegben-az-ero.jpg"
              alt="Egységben az erő — katalizátora az anya. Női elvonulás anyáknak és leendő anyáknak. 2026. augusztus 6–9."
              className="w-full h-auto block"
              style={{ filter: "saturate(0.98) contrast(1.02)" }}
              loading="lazy"
            />
          </div>
        </figure>

        {/* Body */}
        <div className="reading-prose text-[16px] md:text-[17px] leading-[1.9] text-graphite font-body font-light">
          <p>
            Hiszem, hogy egy család ereje nem attól születik meg, hogy mindenki
            egyszerre változik meg. Sokszor elég, ha valaki teret ad egy új minőségnek.
            Ha valaki másképp kezd jelen lenni és először választja a{" "}
            <span className="text-gold">kapcsolódást</span> a megszokott reakciók
            helyett.
          </p>
          <p>
            Édesanyaként különleges helyünk van ebben a rendszerben. Nem azért, mert nekünk kell
            mindent megoldanunk. Hanem mert jelenlétünk minősége nap mint nap formálja azt a
            teret, amelyben a család él. Ezért hiszem, hogy amikor egy anya újra kapcsolódik
            önmagához, az nemcsak róla szól. Hullámokat indít. Kapcsolatokat formál. Lehetőségeket
            nyit. Lassan, szinte észrevétlenül, az egész család új rendet kezd találni.
          </p>
        </div>

        {/* The core insight — matches pull-quote family scale */}
        <div className="my-10 md:my-12 text-center">
          <p className="font-serif-display italic text-graphite text-[22px] md:text-[26px] leading-[1.45] max-w-xl mx-auto">
            Az egység nem attól születik meg, hogy mindenki{" "}
            <em className="text-gold" style={{ fontStyle: "italic" }}>
              egyforma
            </em>{" "}
            lesz.
            <br />
            Hanem attól, hogy mindenki megtalálhatja a{" "}
            <em className="text-gold" style={{ fontStyle: "italic" }}>
              saját helyét
            </em>
            .
          </p>
        </div>

        {/* Bridge line */}
        <p className="text-center font-serif-display italic text-graphite-soft text-[18px] md:text-[20px] leading-[1.5] mb-6">
          Talán ezért érzem azt, hogy…
        </p>

        {/* Final flourish — the phrase from the image */}
        <p
          data-testid="experience-family-unity-flourish"
          className="text-center font-serif-display text-graphite text-[28px] sm:text-[32px] md:text-[36px] leading-[1.2] tracking-tight"
        >
          <em className="text-gold" style={{ fontStyle: "italic" }}>
            Egységben az erő.
          </em>
        </p>

        {/* Simple horizontal line separator between the flourish and practical details */}
        <div className="max-w-sm mx-auto mt-8 md:mt-10 hairline" />
      </div>
    </section>
  );
}

